/**
 * Supabase Auth adapter.
 *
 * Supabase handles the parts that must not be hand-rolled: password hashing,
 * the activation email, token issuance and session refresh. This file's whole
 * job is translation — mapping the platform's vocabulary onto Supabase's, and
 * mapping its errors back onto typed `AuthError` codes so the UI never parses
 * a message string.
 *
 * The account model lives in a `public.accounts` table with row-level security
 * (see `database/auth-schema.sql`); Supabase owns `auth.users`, and we never
 * read or write that table directly.
 */

import type { SupabaseClient, User } from '@supabase/supabase-js';
import { createClient } from '@supabase/supabase-js';
import {
  AuthError,
  SESSION_TTL_MS,
  type Account,
  type AuthProvider,
  type ConsentEmailResult,
  type RegistrationInput,
  type RegistrationResult,
  type Session,
} from './types.ts';
import { kindForAgeBand, normalizeEmail, scorePassword, validateRegistration } from './validate.ts';

export type SupabaseAuthConfig = {
  url: string;
  anonKey: string;
  /**
   * Where the activation link returns the user. Must be an allowed redirect
   * URL in the Supabase dashboard, or the link silently fails.
   */
  redirectTo: string;
  /** Where the password-reset link returns. Also needs allow-listing. */
  resetRedirectTo: string;
};

/**
 * Builds the provider, over a client the caller may already own.
 *
 * Passing one in is the supported way to use this in an application, and the
 * reason is not tidiness. Two clients over the same project share a storage
 * key, and each one runs `detectSessionInUrl` when it is created — so both race
 * to spend the single-use code in an activation link, and the loser reports a
 * link that worked as a link that failed. supabase-js warns about the same
 * hazard itself ("Multiple GoTrueClient instances detected"). One client per
 * browser context, always.
 */
export function createSupabaseAuthProvider(config: SupabaseAuthConfig, client?: SupabaseClient): AuthProvider {
  return new SupabaseAuthProvider(client ?? createClient(config.url, config.anonKey, SUPABASE_CLIENT_OPTIONS), config);
}

/**
 * How a browser client must be configured for the emailed links to work.
 *
 * Exported so the application's own client is built identically to the one this
 * module would have made — a client with `detectSessionInUrl` off would leave
 * the activation code in the address bar unspent.
 */
export const SUPABASE_CLIENT_OPTIONS = {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // The activation link returns a code in the URL; let the client consume it.
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
} as const;

/**
 * Exported for tests only — production code builds one through
 * `createSupabaseAuthProvider`, which owns the client. Taking the client as a
 * constructor argument is what lets the translation logic below be tested
 * against a fake instead of a network.
 */
export class SupabaseAuthProvider implements AuthProvider {
  readonly name = 'Supabase Auth';
  readonly isSecure = true;

  constructor(
    private readonly client: SupabaseClient,
    private readonly config: SupabaseAuthConfig,
  ) {}

  async register(input: RegistrationInput, now: number): Promise<RegistrationResult> {
    // Validate locally first so the student gets an answer without a round trip.
    validateRegistration(input);

    const kind = kindForAgeBand(input.ageBand);
    const { data, error } = await this.client.auth.signUp({
      email: normalizeEmail(input.email),
      password: input.password,
      options: {
        emailRedirectTo: this.config.redirectTo,
        // Deliberately minimal (§29): a chosen name and an age band. No
        // birthday, no school, no location.
        data: {
          display_name: input.displayName.trim(),
          age_band: input.ageBand,
          account_kind: kind,
          ...(input.guardianEmail ? { guardian_email: normalizeEmail(input.guardianEmail) } : {}),
          terms_accepted_at: new Date(now).toISOString(),
        },
      },
    });

    if (error) {
      const translated = translate(error);
      // A project with auto-confirm on answers a repeat sign-up with an error
      // rather than an obfuscated user. Either way the address is taken, and
      // either way we do not know whether that account was ever activated.
      if (translated.code === 'email-taken') throw await this.addressTaken(normalizeEmail(input.email));
      throw translated;
    }
    if (!data.user) throw new AuthError('account-not-found', 'Registration did not return an account.');

    // Supabase returns a user with no identities when the address is already
    // registered — it deliberately does not confirm existence to a stranger.
    if (data.user.identities?.length === 0) {
      throw await this.addressTaken(normalizeEmail(input.email));
    }

    return { account: toAccount(data.user, now) };
  }

  /**
   * Mails a fresh activation link, and does not care whether one went out.
   *
   * Both callers below have reached a dead end an activation link is the way
   * out of, and neither can ask Supabase whether the account behind the
   * address was ever activated — an obfuscated sign-up response looks
   * identical either way, deliberately, because answering would confirm
   * membership to a stranger. So do not guess: `resend` mails a link for an
   * account still awaiting activation and does nothing at all for one that is
   * already active or does not exist, which is exactly the decision needed,
   * made by the only party that can make it.
   *
   * Best effort by design. A rate limit here means a link went out moments
   * ago, which is the outcome that was wanted; any other failure still leaves
   * the resend form on `/activate`. Neither is worth replacing the message the
   * student is about to read.
   */
  private async offerActivationLink(email: string): Promise<void> {
    try {
      await this.client.auth.resend({
        type: 'signup',
        email,
        options: { emailRedirectTo: this.config.redirectTo },
      });
    } catch {
      // Swallowed on purpose: see above.
    }
  }

  /**
   * What a taken address means, and what to do about it.
   *
   * The person who signed up and never got the mail is unstuck by the very act
   * of trying again, which is what they expected in the first place. The
   * message is the one sentence that is true whether or not the account behind
   * the address was ever activated.
   */
  private async addressTaken(email: string): Promise<AuthError> {
    await this.offerActivationLink(email);
    return new AuthError(
      'email-taken',
      'That email is already registered. If it was never activated, a new activation link is on its way — otherwise, sign in instead.',
      'email',
    );
  }

  /**
   * An account that exists, with the right password, that cannot be signed
   * into yet.
   *
   * Unlike a taken address this one is not ambiguous — Supabase checks the
   * password before it reports an unconfirmed email, so reaching here means
   * the account is real, is pending, and belongs to whoever is typing. Send
   * the link rather than describing one they have already failed to receive.
   */
  private async notActivated(email: string): Promise<AuthError> {
    await this.offerActivationLink(email);
    return new AuthError(
      'not-activated',
      'That account has not been activated yet. A new activation link is on its way — follow it, then sign in.',
    );
  }

  /**
   * Completes activation from the code in the redirect URL.
   *
   * Supabase's email link carries a PKCE code rather than a token we mint, so
   * "activate" means exchanging that code for a session.
   */
  async activate(token: string, now: number): Promise<Account> {
    const { data, error } = await this.client.auth.exchangeCodeForSession(token);
    if (error) throw translate(error);
    if (!data.user) throw new AuthError('invalid-token', 'That activation link is not valid.');
    return this.hydrate(data.user, now);
  }

  async resendActivation(email: string, _now: number): Promise<RegistrationResult> {
    const { error } = await this.client.auth.resend({
      type: 'signup',
      email: normalizeEmail(email),
      options: { emailRedirectTo: this.config.redirectTo },
    });
    if (error) throw translate(error);
    // Never returns an account: confirming whether an address is registered
    // would leak membership to anyone who asks.
    return { account: pendingPlaceholder(email) };
  }

  /**
   * Records a guardian's consent from a signed token (§29).
   *
   * The guardian is *not* signed in — they followed a link from an email — so
   * this cannot go through the auth flow. Holding a valid signed token is the
   * authorisation, and the `grant_consent` SQL function verifies the HMAC
   * before recording anything. A child cannot forge one.
   */
  async grantConsent(token: string, now: number): Promise<Account> {
    const { data, error } = await this.client.rpc('grant_consent', { consent_token: token });
    if (error) {
      const message = error.message.toLowerCase();
      if (message.includes('expired')) {
        throw new AuthError('token-expired', 'That permission link has expired. Ask for a new one.');
      }
      throw new AuthError('invalid-token', 'That permission link is not valid.');
    }

    const accountId = (data as { accountId?: string } | null)?.accountId;
    if (!accountId) throw new AuthError('invalid-token', 'That permission link is not valid.');

    return {
      id: accountId,
      email: '',
      kind: 'child',
      status: 'active',
      createdAt: now,
      consentGrantedAt: now,
      profileIds: [],
    };
  }

  /**
   * Asks the server to email the guardian.
   *
   * Sending happens in an Edge Function: the Resend key must never reach a
   * browser, and the consent token must be minted somewhere the child cannot
   * reach. Failure is reported but not fatal — the account still works locally,
   * and the request can be retried from the account page.
   *
   * What it must not do is guess. Every one of these failures once resolved to
   * the same `false` and the account page announced that email was switched
   * off, which was true for exactly one of them and actively misleading for the
   * rest.
   */
  async requestGuardianConsent(): Promise<ConsentEmailResult> {
    const { data, error } = await this.client.functions.invoke('send-guardian-consent');

    if (error) return describeConsentFailure(error);

    if ((data as { alreadyGranted?: boolean } | null)?.alreadyGranted) {
      return {
        sent: false,
        reason: 'already-granted',
        message: 'Your guardian has already given permission — nothing more is needed.',
      };
    }

    return { sent: true, message: 'A new permission email is on its way.' };
  }

  async requestPasswordReset(email: string): Promise<void> {
    const { error } = await this.client.auth.resetPasswordForEmail(normalizeEmail(email), {
      redirectTo: this.config.resetRedirectTo,
    });
    // Rate limiting is worth surfacing; anything else is swallowed on purpose,
    // so a caller cannot learn which addresses are registered by probing.
    if (error && (error.status === 429 || error.message.toLowerCase().includes('rate limit'))) {
      throw translate(error);
    }
  }

  /**
   * Sets a new password.
   *
   * The recovery link has already established a session by the time this runs —
   * the client consumes the credential during initialisation — and that session
   * is what authorises the change. Exchanging the code again here would fail,
   * because Supabase codes are single-use, and would report a working link as
   * invalid.
   */
  async resetPassword(_code: string, newPassword: string): Promise<Account> {
    // Validate first, so a rejected password does not cost the student a fresh
    // email for a mistake they could have been told about immediately.
    const strength = scorePassword(newPassword);
    if (!strength.acceptable) {
      throw new AuthError('weak-password', strength.suggestions[0] ?? 'Please choose a stronger password.', 'password');
    }

    const { data: current } = await this.client.auth.getSession();
    if (!current.session) {
      throw new AuthError('token-expired', 'That reset link has expired or was already used. Request a new one.');
    }

    const { data, error } = await this.client.auth.updateUser({ password: newPassword });
    if (error) {
      if (error.message.toLowerCase().includes('should be different')) {
        throw new AuthError('same-password', 'That is the password you already have. Choose a different one.', 'password');
      }
      throw translate(error);
    }
    if (!data.user) throw new AuthError('account-not-found', 'Account not found.');

    return this.hydrate(data.user, Date.now());
  }

  async signIn(email: string, password: string, now: number): Promise<{ account: Account; session: Session }> {
    const { data, error } = await this.client.auth.signInWithPassword({
      email: normalizeEmail(email),
      password,
    });
    if (error) {
      const translated = translate(error);
      // Supabase verifies the password before it reports an unconfirmed
      // email, so this branch is a real owner locked out of a real account.
      if (translated.code === 'not-activated') throw await this.notActivated(normalizeEmail(email));
      throw translated;
    }
    if (!data.user || !data.session) throw new AuthError('invalid-credentials', 'That email and password do not match.');

    const base = toAccount(data.user, now);
    if (base.status === 'pending') {
      throw await this.notActivated(normalizeEmail(email));
    }

    // Only now — an unactivated account is turned away without costing a query.
    const account = await this.hydrate(data.user, now);

    return {
      account,
      session: {
        accountId: account.id,
        token: data.session.access_token,
        issuedAt: now,
        expiresAt: data.session.expires_at ? data.session.expires_at * 1000 : now + SESSION_TTL_MS,
      },
    };
  }

  async signOut(_session: Session): Promise<void> {
    const { error } = await this.client.auth.signOut();
    if (error) throw translate(error);
  }

  async resolveSession(_token: string, now: number): Promise<Account | null> {
    // The client holds and refreshes the session itself; the token argument is
    // part of the contract for providers that do not.
    const { data, error } = await this.client.auth.getUser();
    if (error || !data.user) return null;
    return this.hydrate(data.user, now);
  }

  /**
   * Watches the client's own idea of who is signed in.
   *
   * Fires for the sign-in nobody called `signIn` for: an activation or recovery
   * link that established a session on arrival, a token refresh, another tab
   * signing out. It also fires once on subscribe with whatever is already
   * there, so a caller needs no separate first read.
   *
   * The callback returns immediately and the work happens after it. supabase-js
   * holds an internal lock for the duration of a listener, and `hydrate` calls
   * back into the same client to read the `accounts` row — doing that inline
   * deadlocks the pair, and the symptom is an application that hangs on load
   * rather than an error anybody can read.
   */
  onSessionChange(listener: (account: Account | null) => void): () => void {
    const { data } = this.client.auth.onAuthStateChange((_event, session) => {
      const user = session?.user;
      setTimeout(() => {
        if (!user) {
          listener(null);
          return;
        }
        void this.hydrate(user, Date.now())
          .then(listener)
          // The metadata account is still a real signed-in user; a failed row
          // read is not a reason to report nobody there.
          .catch(() => listener(toAccount(user, Date.now())));
      }, 0);
    });

    return () => data.subscription.unsubscribe();
  }

  /**
   * The account as the *database* has it, not as the token remembers it.
   *
   * `user_metadata` is a snapshot taken at sign-up and refreshed only when the
   * user themselves updates it. Two things move without it: `grant_consent`
   * records a guardian's approval on `public.accounts`, and `attachProfile`
   * writes `profile_ids` there. Reading either from metadata means never seeing
   * it — which is how a child's account went on saying "waiting for a parent or
   * guardian" after the guardian had already said yes.
   *
   * A failure here is not an error state. If the row cannot be read the
   * metadata account still describes a real signed-in user, and losing the
   * session over one failed select would be far worse than a stale banner.
   */
  private async hydrate(user: User, now: number): Promise<Account> {
    const base = toAccount(user, now);
    try {
      const { data } = await this.client.from('accounts').select('*').eq('id', user.id).maybeSingle();
      return mergeAccountRow(base, data as Record<string, unknown> | null);
    } catch {
      return base;
    }
  }

  async updateEmail(_accountId: string, email: string, now: number): Promise<Account> {
    const { data, error } = await this.client.auth.updateUser({ email: normalizeEmail(email) });
    if (error) throw translate(error);
    if (!data.user) throw new AuthError('account-not-found', 'Account not found.');
    // Supabase re-verifies the new address, so the change is not live yet.
    return toAccount(data.user, now);
  }

  async attachProfile(accountId: string, profileId: string): Promise<Account> {
    return this.setProfiles(accountId, (ids) => (ids.includes(profileId) ? ids : [...ids, profileId]));
  }

  async detachProfile(accountId: string, profileId: string): Promise<Account> {
    return this.setProfiles(accountId, (ids) => ids.filter((id) => id !== profileId));
  }

  /**
   * Reads, updates and returns the account, merged the same way `hydrate` does.
   *
   * Building the answer out of the row alone is the tempting shortcut and it is
   * wrong twice over. `status` on that row is a *mirror*, maintained by a
   * trigger when the email is confirmed — the authority is
   * `auth.users.email_confirmed_at`, and a row the trigger has not caught up
   * with would report an activated account as pending, which the application
   * reads as signed out. And `display_name` and `age_band` live only in
   * metadata, so a row-built account silently drops the two fields a new
   * device uses to rebuild the learner.
   */
  private async setProfiles(accountId: string, update: (ids: string[]) => string[]): Promise<Account> {
    const { data: userData } = await this.client.auth.getUser();
    if (!userData?.user) {
      throw new AuthError('session-expired', 'Your session has expired. Sign in again to continue.');
    }

    const { data: row, error: readError } = await this.client
      .from('accounts')
      .select('*')
      .eq('id', accountId)
      .single();
    if (readError) throw new AuthError('account-not-found', readError.message);

    const next = update((row?.profile_ids as string[]) ?? []);
    const { error } = await this.client.from('accounts').update({ profile_ids: next }).eq('id', accountId);
    if (error) throw new AuthError('account-not-found', error.message);

    return mergeAccountRow(toAccount(userData.user, Date.now()), { ...row, profile_ids: next });
  }

  /**
   * Irreversible deletion (§29).
   *
   * Deleting the auth user requires service-role privileges, which a browser
   * must never hold — so this calls a `delete_account` Postgres function that
   * runs as the definer and removes both the account row and the auth user.
   */
  async deleteAccount(accountId: string): Promise<void> {
    const { error } = await this.client.rpc('delete_account', { account_id: accountId });
    if (error) throw new AuthError('account-not-found', error.message);
    await this.client.auth.signOut();
  }
}

/* -------------------------------------------------------------------------- */
/* Translation                                                                */
/* -------------------------------------------------------------------------- */

function toAccount(user: User, now: number): Account {
  const meta = user.user_metadata ?? {};
  const confirmed = Boolean(user.email_confirmed_at ?? user.confirmed_at);
  return {
    id: user.id,
    email: user.email ?? '',
    kind: (meta.account_kind as Account['kind']) ?? 'self',
    status: confirmed ? 'active' : 'pending',
    createdAt: user.created_at ? Date.parse(user.created_at) : now,
    ...(confirmed && user.email_confirmed_at ? { activatedAt: Date.parse(user.email_confirmed_at) } : {}),
    ...(meta.guardian_email ? { guardianEmail: String(meta.guardian_email) } : {}),
    ...(meta.display_name ? { displayName: String(meta.display_name) } : {}),
    ...(meta.age_band ? { ageBand: meta.age_band as Account['ageBand'] } : {}),
    ...(meta.consent_granted_at ? { consentGrantedAt: Date.parse(String(meta.consent_granted_at)) } : {}),
    profileIds: Array.isArray(meta.profile_ids) ? (meta.profile_ids as string[]) : [],
  };
}

/**
 * Overlays the `accounts` row onto the account derived from the token.
 *
 * Where the two disagree the row wins, and for `consent_granted_at` it wins
 * even when it is empty: the row is the record of whether a guardian approved,
 * so a row saying nothing means nothing was approved. Metadata must never be
 * able to assert consent, because a signed-in user can write their own
 * metadata — `auth.updateUser({ data })` is theirs to call.
 *
 * `guardianEmail` is the lenient exception. It is display-only, it cannot
 * authorise anything, and falling back to metadata keeps the address on screen
 * for a row written before the trigger populated it.
 */
export function mergeAccountRow(base: Account, row: Record<string, unknown> | null | undefined): Account {
  if (!row) return base;

  const merged: Account = { ...base };

  if (typeof row.kind === 'string') merged.kind = row.kind as Account['kind'];
  if (Array.isArray(row.profile_ids)) merged.profileIds = row.profile_ids as string[];
  if (row.guardian_email) merged.guardianEmail = String(row.guardian_email);

  const granted = row.consent_granted_at ? Date.parse(String(row.consent_granted_at)) : NaN;
  if (Number.isFinite(granted)) merged.consentGrantedAt = granted;
  else delete merged.consentGrantedAt;

  return merged;
}

const pendingPlaceholder = (email: string): Account => ({
  id: '',
  email: normalizeEmail(email),
  kind: 'self',
  status: 'pending',
  createdAt: Date.now(),
  profileIds: [],
});

/**
 * Turns a failed `send-guardian-consent` invocation into something a learner
 * can act on.
 *
 * `functions.invoke` reports three shapes: a fetch that never completed
 * (offline, blocked, or a CORS refusal — the browser will not say which), a
 * relay failure, and an HTTP status with our own function's JSON body behind
 * it. Only the last knows anything, so the status is what the mapping turns on
 * and the function's own sentence is the fallback.
 */
export async function describeConsentFailure(error: unknown): Promise<ConsentEmailResult> {
  const name = (error as { name?: string } | null)?.name;

  if (name === 'FunctionsFetchError' || name === 'FunctionsRelayError') {
    return {
      sent: false,
      reason: 'unreachable',
      message:
        'We could not reach the server to send it. Check your connection and try again — your work here is unaffected.',
    };
  }

  const response = (error as { context?: Response } | null)?.context;
  const status = typeof response?.status === 'number' ? response.status : 0;
  const served = await readErrorMessage(response);

  switch (status) {
    case 401:
      return {
        sent: false,
        reason: 'not-signed-in',
        message: 'Your session has expired. Sign in again and we can send it.',
      };
    case 404:
      return {
        sent: false,
        reason: 'account-not-found',
        message: 'We could not find this account on the server. Sign out and back in, then try again.',
      };
    case 400:
      return served.toLowerCase().includes('guardian address')
        ? {
            sent: false,
            reason: 'no-guardian',
            message: 'No parent or guardian address is recorded for this account, so there is nobody to email.',
          }
        : {
            sent: false,
            reason: 'not-needed',
            message: 'This account does not need a guardian’s permission.',
          };
    case 500:
      return {
        sent: false,
        reason: 'not-configured',
        message: 'Email is not switched on for this deployment, so we could not send it.',
      };
    case 502:
      return {
        sent: false,
        reason: 'send-failed',
        message: 'The mail service turned the message away. Please try again in a few minutes.',
      };
    default:
      return {
        sent: false,
        reason: 'unknown',
        message: served || 'We could not send it just now. Please try again in a few minutes.',
      };
  }
}

/**
 * The `{ error }` string our Edge Functions return.
 *
 * Best effort by design: a gateway failure answers with HTML, and a body may
 * already be consumed. Neither is worth throwing from inside an error handler.
 */
async function readErrorMessage(response: Response | undefined): Promise<string> {
  if (!response) return '';
  try {
    const body = (await response.clone().json()) as { error?: unknown; message?: unknown };
    const value = body?.error ?? body?.message;
    return typeof value === 'string' ? value : '';
  } catch {
    return '';
  }
}

/**
 * Maps Supabase's messages onto typed codes.
 *
 * Matching on message text is unpleasant but unavoidable — Supabase's error
 * codes are not stable across versions, and a wrong-password error that reaches
 * the student as a raw server string is worse.
 */
export function translate(error: { message: string; status?: number }): AuthError {
  const message = error.message.toLowerCase();

  if (message.includes('already registered') || message.includes('already been registered')) {
    return new AuthError('email-taken', 'That email is already registered. Try signing in instead.', 'email');
  }
  if (message.includes('invalid login credentials') || message.includes('invalid email or password')) {
    return new AuthError('invalid-credentials', 'That email and password do not match.');
  }
  if (message.includes('email not confirmed')) {
    return new AuthError('not-activated', 'Check your email and follow the activation link before signing in.');
  }
  if (message.includes('expired')) {
    return new AuthError('token-expired', 'That link has expired. Request a new one.');
  }
  if (message.includes('rate limit') || error.status === 429) {
    return new AuthError('too-many-attempts', 'Too many attempts just now. Please wait a minute and try again.');
  }
  if (message.includes('password')) {
    return new AuthError('weak-password', error.message, 'password');
  }
  if (message.includes('invalid') && message.includes('email')) {
    return new AuthError('invalid-email', 'That does not look like an email address.', 'email');
  }
  if (message.includes('token') || message.includes('code')) {
    return new AuthError('invalid-token', 'That link is not valid. Request a new one.');
  }

  return new AuthError('not-supported', error.message);
}
